import cn from 'classnames';
import { range } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage as Message } from 'react-intl';
import Markdown from '../../../../../components/Markdown';
import Text from '../../../../../components/Text';
import { RootContext } from '../../../../../contexts/rootContext';
import { getColumnsCount } from '../../../../root/wrapper';
import styles from './MatrixHeader.module.css';

interface MatrixHeaderProps {
  question: {
    type: string;
    hasHints?: boolean;
    lowerHint?: string;
    higherHint?: string;
    answers?: {
      id: string;
      text?: string;
      exclusiveAnswer?: boolean;
    }[];
  };
}

export const MatrixHeader: React.FC<MatrixHeaderProps> = ({ question }) => {
  const { schemaEngine } = useContext(RootContext);
  const { answers = [], type, hasHints, lowerHint, higherHint } = question;
  const columnsCount = getColumnsCount(schemaEngine, question);

  const exclusiveAnswers = answers.filter(it => it.exclusiveAnswer);
  const contentColumnsCount = columnsCount - exclusiveAnswers.length;

  const ratingTitle = schemaEngine.hasTrait(type, 'RATING_TYPE');

  if (ratingTitle) {
    const colSpan = Math.floor(contentColumnsCount / 2);
    const leftColSpan: React.CSSProperties = {
      textAlign: 'left',
      justifyContent: 'flex-start',
      gridColumnStart: 2,
      gridColumnEnd: 2 + colSpan,
      paddingLeft: 0,
    };
    const rightColSpan: React.CSSProperties = {
      textAlign: 'right',
      justifyContent: 'flex-end',
      gridColumnStart: 2 + colSpan,
      gridColumnEnd: 2 + contentColumnsCount,
      paddingRight: 0,
    };

    return (
      <>
        <Answer />

        <Answer style={leftColSpan}>{hasHints ? <Markdown>{lowerHint}</Markdown> : null}</Answer>
        <Answer style={rightColSpan}>{hasHints ? <Markdown>{higherHint}</Markdown> : null}</Answer>
        {exclusiveAnswers.map((answer, index) => (
          <Answer
            key={answer.id}
            className={cn({
              [c(styles.firstExclusive)]: index === 0,
              [c(styles.lastColumn)]: index === exclusiveAnswers.length - 1,
            })}
          >
            {answer.text || <Message id={'System.NotApplicable'} />}
          </Answer>
        ))}
      </>
    );
  }

  return (
    <>
      <Answer className={styles.firstColumn} />

      {range(columnsCount).map(index => {
        const answer = answers[index];
        const isLast = index === columnsCount - 1;

        if (answer?.exclusiveAnswer) {
          return <Answer key={answer.id}>{answer.text || <Message id={'System.NotApplicable'} />}</Answer>;
        }

        return (
          <Answer key={answer?.id ?? index} className={cn({ [c(styles.lastColumn)]: isLast })}>
            <Markdown>{answer?.text ?? ''}</Markdown>
          </Answer>
        );
      })}
    </>
  );
};

const Answer: React.FC<{
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}> = ({ style, children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSticky, setSticky] = useState(false);
  const [cloneStyle, setCloneStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const stickyAnswer = ref.current;

    if (!stickyAnswer) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }

        const { height, width, left } = entry.target.getBoundingClientRect();
        setCloneStyle({ height, width, left });
        setSticky(entry.intersectionRatio < 1);
      },
      { threshold: 1 },
    );

    setTimeout(() => {
      // FIXME: задержка для корректной обработки smooth scroll
      observer.observe(stickyAnswer);
    }, 500);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Text
        ref={ref}
        variant="body"
        style={style}
        className={cn(className, styles.answer, {
          [c(styles.answerSticky)]: isSticky,
        })}
      >
        {children}
      </Text>

      <Text
        variant="body"
        style={{ ...style, ...cloneStyle }}
        className={cn(className, styles.answer, styles.answerClone, {
          [c(styles.answerCloneSticky)]: isSticky,
        })}
      >
        {children}
      </Text>
    </>
  );
};
